import type { ISendEventResponse } from 'matrix-js-sdk';

export type Kind =
  | 'text'
  | 'emote'
  | 'notice'
  | 'image'
  | 'file'
  | 'audio'
  | 'video'
  | 'location'
  | 'other';
export type MessageStatus = 'read' | 'sent' | 'sending' | 'error';

export const UserState = {
  Online: 'online',
  Unavailable: 'unavailable',
  Offline: 'offline'
} as const;

export type UserState = typeof UserState[keyof typeof UserState];

export interface ChatUser {
  uid: string;
  firstname: string;
  lastname: string;
  displayName?: string;
  isSender: boolean;
}

export type MessageReaction = {
  key: string;
  count: number;
  ownEventId?: string;
  sender: Array<ChatUser>;
};

/** Toggles a reaction on a message. */
export type ReactionClickHandler = (
  eventId: string,
  reactionKey: string,
  roomId?: string | undefined
) => Promise<ISendEventResponse | undefined>;

export interface AnyMessageGeneric<
  T extends Kind,
  U extends ChatUser,
  C extends Record<string, unknown>
> {
  id?: string;
  txnId?: string;
  roomId?: string;
  kind: T;
  user: U;
  content: C;
  date: Date;
  error?: string;
  status?: MessageStatus;
  reactions?: Array<MessageReaction>;
}

export type AnyMessage = AnyMessageGeneric<
  Kind,
  ChatUser,
  Record<string, unknown>
>;

export type TextMessage = AnyMessageGeneric<
  'text',
  ChatUser,
  {
    text: string;
    isUndecryptable?: boolean;
  }
>;

export type NoticeMessage = AnyMessageGeneric<
  'notice',
  ChatUser,
  {
    text: string;
    icon?: string;
    noticeType?: string;
  }
>;

export function isTextMessage(message: AnyMessage): message is TextMessage {
  return message.kind === 'text';
}

export function isNoticeMessage(message: AnyMessage): message is NoticeMessage {
  return message.kind === 'notice';
}

export type FileMessage = AnyMessageGeneric<
  'image' | 'file' | 'audio' | 'video',
  ChatUser,
  {
    text: string;
    loadUrl: () => Promise<string | undefined>;
    mimeType?: string;
    size?: number;
  }
>;

const FILE_MESSAGE_KINDS: Kind[] = ['image', 'file', 'audio', 'video'];

export function isFileMessage(message: AnyMessage): message is FileMessage {
  return FILE_MESSAGE_KINDS.includes(message.kind);
}
